// src/lib/constants/analytics.ts
//
// New Analytics selector vocabularies and query-key factory. Wire selectors are
// STRICT camelCase (no snake aliases) per the gateway contract
// (ref_analytics_gateway_public_contract.md).

export const ANALYTICS_METRICS = ['watchTime', 'sessions', 'uniqueViewers'] as const;
export type AnalyticsMetric = typeof ANALYTICS_METRICS[number];

export const ANALYTICS_METRIC_LABELS: Record<AnalyticsMetric, string> = {
  watchTime: 'Watch time',
  sessions: 'Sessions',
  uniqueViewers: 'Unique viewers',
};

// region unlocked post-release (gateway ADR-0009 / notes adr_014); city joined
// with the per-media geo release (Seq U-5, Go ADR-0011 — served by the
// media-table rollup, same wire shape); embedDomain joined with the metrics
// expansion (row values: lowercased hostnames or the literal '(direct)').
export const ANALYTICS_DIMENSIONS = ['country', 'region', 'city', 'deviceType', 'platform', 'streamType', 'embedDomain'] as const;
export type AnalyticsDimension = typeof ANALYTICS_DIMENSIONS[number];

// The Audience tab's grouped dimension control (plan §4.4) and the media
// Drawer's breakdown panel. Geo dimensions alone drive the map presentation;
// the technology dimensions are account-grain only and rejected on the
// per-media route; the source group (embedDomain, metrics expansion) is
// exposed only behind FEATURE_FLAGS.analyticsExpansionEnabled.
export const ANALYTICS_GEO_DIMENSIONS = ['country', 'region', 'city'] as const;
export type AnalyticsGeoDimension = typeof ANALYTICS_GEO_DIMENSIONS[number];
export const ANALYTICS_TECH_DIMENSIONS = ['deviceType', 'platform', 'streamType'] as const;
export const ANALYTICS_SOURCE_DIMENSIONS = ['embedDomain'] as const;

/**
 * The per-media audience route's vocabulary: geo + embedDomain, and — once the
 * gateway declares `features.hourly` (the event-window release, api
 * ADR-0015) — the technology dimensions too, materialized at media grain.
 * The Drawer and the per-stream page expose the tech group only behind that
 * runtime feature; the type is the full union.
 */
export const ANALYTICS_MEDIA_AUDIENCE_DIMENSIONS = [
  ...ANALYTICS_GEO_DIMENSIONS,
  ...ANALYTICS_TECH_DIMENSIONS,
  ...ANALYTICS_SOURCE_DIMENSIONS,
] as const;
export type AnalyticsMediaAudienceDimension = typeof ANALYTICS_MEDIA_AUDIENCE_DIMENSIONS[number];
/** The pre-event-window per-media vocabulary (gateway without `features.hourly`). */
export const ANALYTICS_MEDIA_AUDIENCE_DIMENSIONS_LEGACY = [
  ...ANALYTICS_GEO_DIMENSIONS,
  ...ANALYTICS_SOURCE_DIMENSIONS,
] as const;

/** Event windows (playback/window, playback/hourly): the gateway caps hour-grain ranges at 92 days. */
export const HOURLY_MAX_RANGE_DAYS = 92;
/** Broadcast padding — "the hour before to the hour after" (the named customer ask). */
export const BROADCAST_PAD_SECONDS = 3600;
/** Trend grain switch for an event window: at or under this span the page charts hours. */
export const HOURLY_TREND_MAX_DAYS = 7;
/** The gateway's chart-point cap for a concurrency series; minute buckets only while the window fits, and the client never gap-fills past it. */
export const CONCURRENCY_MAX_CHART_POINTS = 2000;
/** Broadcast picker lookback (presence retention) — floored to the product window when configured. */
export const BROADCAST_LOOKBACK_DAYS = 90;
/** A broadcast whose run ended within this many seconds of now is shown as "In progress". */
export const BROADCAST_IN_PROGRESS_GRACE_SECONDS = 600;
/** Broadcast list refresh while a run is in progress (otherwise never polled). */
export const BROADCASTS_LIVE_REFETCH_MS = 60_000;
/** Stream report window modes (dashboard ADR-009, amended 2026-09-13). */
export const STREAM_WINDOW_MODES = ['broadcast', 'day', 'range'] as const;
export type StreamWindowMode = typeof STREAM_WINDOW_MODES[number];
export const STREAM_WINDOW_MODE_LABELS: Record<StreamWindowMode, string> = {
  broadcast: 'Broadcast',
  day: 'Day',
  range: 'Range',
};
/**
 * Broadcast shaping (client-side, over the gateway's presence runs). The server
 * bridges gaps of 10 min; an always-on channel still fragments whenever nobody
 * watches for longer, so the picker re-joins runs separated by ≤ 45 min
 * (a halftime or an intermission stays one broadcast).
 */
export const BROADCAST_MERGE_GAP_SECONDS = 45 * 60;
/** Merged runs shorter than this are noise (a viewer testing the player), not a broadcast. */
export const BROADCAST_MIN_MINUTES = 15;
/** A merged run at least this long marks a 24/7 stream — no event has a 20-hour audience run. */
export const BROADCAST_CONTINUOUS_RUN_HOURS = 20;
/** Alternatively: merged runs covering at least this share of the lookback span = always on. */
export const BROADCAST_CONTINUOUS_COVERAGE_RATIO = 0.5;

export const ANALYTICS_INTERVALS = ['auto', 'minute', 'hour'] as const;
export type AnalyticsInterval = typeof ANALYTICS_INTERVALS[number];

export type AnalyticsScopeKind = 'account' | 'network';

/** Gateway row limits (clamped downstream as well). Top-content max raised
 * 100 -> 500 (2026-08, legacy-reports parity) in lockstep with the Go
 * MaxTopContentLimit — the serving-side clamp silently caps anything higher. */
export const TOP_CONTENT_DEFAULT_LIMIT = 20;
export const TOP_CONTENT_MAX_LIMIT = 500;
/** CSV export always requests the full server export ceiling (Go MaxExportLimit
 * / operator API_MAX_PAGE_SIZE), independent of the on-screen row selection. */
export const TOP_CONTENT_EXPORT_LIMIT = 500;
export const SUBACCOUNTS_DEFAULT_LIMIT = 50;
export const SUBACCOUNTS_MAX_LIMIT = 100;

/** Live data polls exactly once per 60 s (phase-plan bound; never faster). */
export const ANALYTICS_LIVE_POLL_INTERVAL_MS = 60_000;

/** A live snapshot older than this is flagged stale in the UI (~3 min). */
export const ANALYTICS_LIVE_STALE_AFTER_SECONDS = 180;

/**
 * Hierarchical TanStack Query keys (ADR-003 §6). Every scoped key embeds the
 * literal 'byCustomer' (consumed by AuthProvider.setSelectedCId's invalidation
 * predicate) plus the scope kind and cid, so keys can never collide across
 * customer selections. Account-only views pin kind to 'account'; the network
 * leaderboard pins 'network'.
 */
export const ANALYTICS_QUERY_KEYS = {
  all: ['analytics'] as const,
  // Global, not per-customer: /meta is account-independent gateway metadata.
  meta: () => [...ANALYTICS_QUERY_KEYS.all, 'meta'] as const,
  scoped: (kind: AnalyticsScopeKind, cid: number) =>
    [...ANALYTICS_QUERY_KEYS.all, 'byCustomer', kind, cid] as const,
  // tz (dashboard ADR-009): the local-day zone the request carried, or 'utc'
  // — a response-affecting selector, so it is part of the key.
  overview: (kind: AnalyticsScopeKind, cid: number, from: number, to: number, tz: string = 'utc') =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'overview', from, to, tz] as const,
  playbackDaily: (kind: AnalyticsScopeKind, cid: number, from: number, to: number, tz: string = 'utc') =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'playbackDaily', from, to, tz] as const,
  topContent: (
    kind: AnalyticsScopeKind,
    cid: number,
    from: number,
    to: number,
    metric: AnalyticsMetric,
    limit: number,
    tz: string = 'utc'
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'topContent', from, to, metric, limit, tz] as const,
  // Event-window surface (api ADR-0015 / dashboard ADR-009). mediaId stays the wire STRING.
  mediaPlaybackDaily: (cid: number, mediaId: string, from: number, to: number, tz: string = 'utc') =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'mediaPlaybackDaily', mediaId, from, to, tz] as const,
  mediaPlaybackHourly: (cid: number, mediaId: string, from: number, to: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'mediaPlaybackHourly', mediaId, from, to] as const,
  mediaPlaybackWindow: (cid: number, mediaId: string, from: number, to: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'mediaPlaybackWindow', mediaId, from, to] as const,
  mediaConcurrency: (cid: number, mediaId: string, from: number, to: number, interval: AnalyticsInterval) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'mediaConcurrency', mediaId, from, to, interval] as const,
  mediaBroadcasts: (cid: number, mediaId: string, from: number, to: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'mediaBroadcasts', mediaId, from, to] as const,
  heatmapByMedia: (cid: number, mediaId: string, from: number, to: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'heatmapByMedia', mediaId, from, to] as const,
  concurrency: (
    kind: AnalyticsScopeKind,
    cid: number,
    from: number,
    to: number,
    interval: AnalyticsInterval
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'concurrency', from, to, interval] as const,
  live: (kind: AnalyticsScopeKind, cid: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'live'] as const,
  liveByMedia: (cid: number, limit: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'liveByMedia', limit] as const,
  audience: (
    cid: number,
    from: number,
    to: number,
    dimension: AnalyticsDimension,
    metric: AnalyticsMetric
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'audience', dimension, metric, from, to] as const,
  // mediaId stays the wire STRING (never a Number) — inside query keys too.
  mediaAudience: (
    cid: number,
    mediaId: string,
    from: number,
    to: number,
    dimension: AnalyticsMediaAudienceDimension,
    metric: AnalyticsMetric
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'mediaAudience', mediaId, dimension, metric, from, to] as const,
  mediaPeaks: (cid: number, mediaId: string, from: number, to: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'mediaPeaks', mediaId, from, to] as const,
  mediaRetention: (cid: number, mediaId: string, from: number, to: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'mediaRetention', mediaId, from, to] as const,
  heatmap: (cid: number, from: number, to: number) =>
    [...ANALYTICS_QUERY_KEYS.scoped('account', cid), 'heatmap', from, to] as const,
  transfer: (
    kind: AnalyticsScopeKind,
    cid: number,
    from: number,
    to: number,
    finalizedOnly: boolean
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped(kind, cid), 'transfer', from, to, finalizedOnly] as const,
  subaccounts: (
    cid: number,
    from: number,
    to: number,
    metric: AnalyticsMetric,
    limit: number
  ) =>
    [...ANALYTICS_QUERY_KEYS.scoped('network', cid), 'subaccounts', from, to, metric, limit] as const,
} as const;
